import { useAuth } from '../auth/Auth'
import { useData } from '../data/DataProvider'
import UserSettings from '../models/UserSettings'

export const useUserAccess = () => {
    const { 
        userRole, 
        ROLES, 
        isConsumer,
        isConsumerGuest,
        isConsumerOrGuest
    } = useAuth()
    
    const { userSettings: rawUserSettings, userWallet } = useData()
    
    const userSettings = rawUserSettings ? UserSettings.fromJSON(rawUserSettings) : null

    const hasFullAccess = () => {
        return userRole === ROLES.CONSUMER
    }

    const hasGuestAccess = () => {
        return userRole === ROLES.CONSUMER_GUEST
    }

    const canAccessBilling = () => {
        return hasFullAccess()
    }

    const canModifySettings = () => {
        return hasFullAccess()
    }

    const canViewFullHistory = () => {
        return hasFullAccess()
    }

    const canAccessReservedCharging = () => {
        return userSettings?.hasReservedChargingAccess() || false
    }

    const canAccessSharedCharging = () => {
        return userSettings?.hasSharedChargingAccess() || false
    }

    const isGuestAccessExpired = () => {
        return userSettings?.isSharedChargingExpired() || false
    }

    const canUseSharedCharger = (model) => {
        const list = userSettings?.getSharedChargingDevices?.() ?? [];
        return Array.isArray(list) && list.includes(model);
    };

    const canAccessAnyCharging = () => {
        return userSettings?.hasAnyChargingAccess() || false
    }

    const getChargingAccessStatus = () => {
        if (!userSettings) return 'loading'
        
        if (userSettings.hasReservedChargingAccess()) {
            return 'reserved'
        }
        
        if (userSettings.hasSharedChargingAccess()) {
            return 'shared'
        }
        
        return 'none'
    }

    const getAccessRestrictions = () => {
        const restrictions = []
        
        if (hasGuestAccess()) {
            restrictions.push('billing')
            restrictions.push('settings_modification')
            restrictions.push('full_history')
            restrictions.push('vehicle_management')
        }
        
        if (!canAccessReservedCharging()) {
            restrictions.push('reserved_charging')
        }
        
        if (!canAccessSharedCharging()) {
            restrictions.push('shared_charging')
        }
        
        return restrictions
    }

    const hasValidCreditCard = () => {
        if (!userWallet || !userWallet.paymentMethods || userWallet.paymentMethods.length === 0) {
            return false
        }
        
        return userWallet.paymentMethods.some(method => 
            method.metadata?.cardOk !== false
        )
    }

    const getCreditCardStatus = () => {
        if (!userWallet || !userWallet.paymentMethods) {
            return 'loading'
        }
        
        if (userWallet.paymentMethods.length === 0) {
            return 'no_card'
        }
        
        const hasValidCard = userWallet.paymentMethods.some(method => 
            method.metadata?.cardOk !== false
        )
        
        if (hasValidCard) {
            return 'valid'
        }
        
        return 'invalid'
    }

    const getInvalidCards = () => {
        if (!userWallet || !userWallet.paymentMethods) {
            return []
        }
        
        return userWallet.paymentMethods.filter(method => 
            method.metadata?.cardOk === false
        )
    }

    return {
        userRole,
        ROLES,
        userSettings,
        isConsumer,
        isConsumerGuest,
        isConsumerOrGuest,
        isGuestAccessExpired,
        canUseSharedCharger,
        hasFullAccess,
        hasGuestAccess,
        canAccessBilling,
        canModifySettings,
        canViewFullHistory,
        canAccessReservedCharging,
        canAccessSharedCharging,
        canAccessAnyCharging,
        getChargingAccessStatus,
        getAccessRestrictions,
        hasValidCreditCard,
        getCreditCardStatus,
        getInvalidCards
    }
}
